import { useEffect, useRef } from "react";

/** Must match procore_integration.channelmanager company channel prefix */
function companyChannel(companyId) {
  return `company:${companyId}`;
}

/**
 * Subscribe to django-eventstream (Server-Sent Events).
 * One channel per company; event names (e.g. change_log_updated) carry the payload type.
 *
 * @param {Object} options
 * @param {number[]} [options.companyIds] - Company IDs (one ?channel= per id)
 * @param {(data: object) => void} [options.onConnected] - When the EventSource opens
 * @param {(data: object) => void} [options.onNotificationNew] - notification_new payload
 * @param {(data: { company_id: number, log?: object }) => void} [options.onChangeLogUpdated] - change_log_updated payload
 * @param {boolean} [options.enabled=true] - If false, does not connect
 */
export function useEventStream({
  companyIds = [],
  onConnected,
  onNotificationNew,
  onChangeLogUpdated,
  enabled = true,
}) {
  const eventSourceRef = useRef(null);
  const onConnectedRef = useRef(onConnected);
  const onNotificationNewRef = useRef(onNotificationNew);
  const onChangeLogUpdatedRef = useRef(onChangeLogUpdated);
  onConnectedRef.current = onConnected;
  onNotificationNewRef.current = onNotificationNew;
  onChangeLogUpdatedRef.current = onChangeLogUpdated;

  const companyIdsKey = companyIds.join(",");

  useEffect(() => {
    if (!enabled || !companyIds.length) return;

    const base = process.env.REACT_APP_API_BASE_URL || "";
    const params = new URLSearchParams();
    for (const id of companyIds) {
      const n = Number(id);
      if (!Number.isFinite(n)) continue;
      params.append("channel", companyChannel(n));
    }
    if (!params.has("channel")) return;

    const url = `${base.replace(/\/$/, "")}/events/?${params.toString()}`;

    const es = new EventSource(url, { withCredentials: true });
    eventSourceRef.current = es;

    es.onopen = () => {
      onConnectedRef.current?.({});
    };

    function parseEventData(e) {
      if (!e?.data) return null;
      try {
        const parsed = JSON.parse(e.data);
        return parsed && typeof parsed === "object" ? parsed : null;
      } catch {
        return null;
      }
    }

    es.addEventListener("error", (e) => {
      parseEventData(e);
    });

    es.addEventListener("notification_new", (e) => {
      const parsed = parseEventData(e);
      const payload = parsed && typeof parsed === "object" ? parsed : {};
      onNotificationNewRef.current?.(payload);
    });

    es.addEventListener("change_log_updated", (e) => {
      const parsed = parseEventData(e);
      const payload = parsed && typeof parsed === "object" ? parsed : {};
      onChangeLogUpdatedRef.current?.(payload);
    });

    return () => {
      es.close();
      eventSourceRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- companyIdsKey serializes companyIds; the array ref from parents is often unstable
  }, [enabled, companyIdsKey]);
}
